// «Выбор для карточки полотна» (README §8). Карточка Конструктора кладёт
// выбор в стор Конструктора (`useFlowStore.libraryPick`, зона canvas —
// docs/api-flow.md §8), а «в схему» зовёт `fillFromLibrary`. Стор Конструктора
// подхватываем через import.meta.glob: если его нет в сборке (ветка без
// canvas), работает запасной путь — `appStore.libraryPick` + событие
// `abx:library-pick` и переход в раздел flow.

import { useAppStore } from '../../store/appStore'
import type { LibraryItem } from './types'

interface FlowPick { scheme: string; node: string; type?: string; q?: string }
interface FlowStoreLike {
  (selector: (s: FlowStateLike) => unknown): unknown
  getState: () => FlowStateLike
  setState: (patch: Partial<FlowStateLike>) => void
}
interface FlowStateLike {
  libraryPick: FlowPick | null
  scheme?: { nodes: { id: string; title: string }[] } | null
  fillFromLibrary?: (t: { name: string; title?: string; description?: string; source?: string }) => Promise<void>
}

const mods = import.meta.glob('../flow/store.ts', { eager: true }) as Record<string, { useFlowStore?: FlowStoreLike }>
const flowStore: FlowStoreLike | undefined = Object.values(mods)[0]?.useFlowStore

export interface Pick {
  node: string
  scheme: string
  /** название элемента на полотне */
  title: string
  /** тип элемента → вкладка каталога */
  tab: 'skill' | 'agent' | 'mcp' | null
  q: string
}

const TAB: Record<string, Pick['tab']> = {
  skill: 'skill', subagent: 'agent', sub: 'agent', mcp: 'mcp',
}

function fromFlow(): Pick | null {
  if (!flowStore) return null
  const s = flowStore.getState()
  const p = s.libraryPick
  if (!p) return null
  const title = s.scheme?.nodes.find((n) => n.id === p.node)?.title ?? ''
  return { node: p.node, scheme: p.scheme, title, tab: TAB[p.type ?? ''] ?? null, q: p.q ?? '' }
}

/** Текущий выбор (реактивно). */
export const usePick: () => Pick | null = flowStore
  ? () => {
      // подписка на сам libraryPick и на название узла
      flowStore((s) => s.libraryPick)
      flowStore((s) => s.scheme)
      return fromFlow()
    }
  : () => {
      const p = useAppStore((s) => s.libraryPick)
      return p ? { node: p.node, scheme: p.scheme, title: p.title, tab: null, q: '' } : null
    }

/** «в схему»: подставить выбранное в элемент и вернуться на полотно. */
export async function fillPick(item: LibraryItem): Promise<void> {
  const app = useAppStore.getState()
  const pick = flowStore ? fromFlow() : app.libraryPick
  if (!pick) return
  const payload = { name: item.name, title: item.title, description: item.description, source: item.homepage ?? item.source }
  if (flowStore?.getState().fillFromLibrary) {
    await flowStore.getState().fillFromLibrary!(payload)
    return
  }
  window.dispatchEvent(new CustomEvent('abx:library-pick', { detail: { scheme: pick.scheme, node: pick.node, item: payload } }))
  app.setLibraryPick(null)
  app.setSection('flow')
}

/** Отказаться от выбора (× на плашке). */
export function cancelPick(): void {
  if (flowStore) flowStore.setState({ libraryPick: null })
  useAppStore.getState().setLibraryPick(null)
}
